/**
 * Enterprise Portal - /enterprise/[slug]
 * 
 * Features:
 * - Dashboard with usage stats
 * - API Keys (Sandbox + Production)
 * - Merchant management
 * - Vira On-Call support
 * - Billing and plan info
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  Key,
  Users,
  BarChart3,
  MessageSquare,
  Settings,
  LogOut,
  Copy,
  Check,
  Eye,
  EyeOff,
  Plus,
  AlertCircle,
  Loader2,
  Send,
  ChevronRight,
  RefreshCw,
  Zap,
  Shield,
  Play,
  Lock,
  X,
  Mail,
  Trash2,
  XCircle,
  CreditCard,
  Crown,
  Rocket,
  Building,
  HelpCircle,
  BookOpen,
  Download,
  FileText,
  Store,
  Target,
  DollarSign
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import DocumentsSection from "@/components/DocumentsSection";

// Enterprise Guide Steps
const enterpriseGuideSteps = [
  {
    title: "1. Set Up Your Account",
    icon: Settings,
    description: "Configure your enterprise settings, add tech support emails, and manage API keys.",
    tips: ["Add tech emails for escalation notifications", "Keep your API key secure", "Review your plan benefits"]
  },
  {
    title: "2. Create Merchant Packages",
    icon: FileText,
    description: "Define Basic, Standard, and Premium packages with custom credit allocations and pricing.",
    tips: ["Set post credits and video credits per package", "Define your pricing in your local currency", "Enable auto-allocation for automatic monthly distribution"]
  },
  {
    title: "3. Onboard Merchants",
    icon: Store,
    description: "Add merchants and assign them to packages. Share their access codes to get started.",
    tips: ["Each merchant gets a unique access code (M-XXXXXXXX)", "Assign packages based on their needs", "Share the Merchant Onboarding Guide with them"]
  },
  {
    title: "4. Allocate Credits",
    icon: Zap,
    description: "Manually allocate credits or enable auto-allocation for monthly distribution.",
    tips: ["Click '+ Credits' to manually allocate", "Auto-allocation runs on subscription renewal", "Monitor usage to prevent exhaustion"]
  },
  {
    title: "5. Monitor & Support",
    icon: MessageSquare,
    description: "Use Vira On-Call for 24/7 support. Escalate complex issues to human support.",
    tips: ["Vira handles common questions instantly", "Escalations are sent to your tech email", "Chat transcripts are preserved"]
  },
  {
    title: "6. Manage Billing",
    icon: DollarSign,
    description: "View plans, purchase additional packages, or upgrade anytime.",
    tips: ["Annual billing saves up to 10%", "Enable auto-subscribe to avoid gaps", "Purchase additional packages as needed"]
  }
];

// Tech Emails Manager Component
function TechEmailsManager({ techEmails, enterpriseId, onUpdate }) {
  const [emails, setEmails] = useState(techEmails || []);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    setEmails(techEmails || []);
  }, [techEmails]);
  
  const addEmail = async () => {
    if (!newEmail || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(newEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    if (emails.includes(newEmail.toLowerCase())) {
      toast.error("This email is already added");
      return;
    }
    
    setSaving(true);
    try {
      const updatedEmails = [...emails, newEmail.toLowerCase()];
      await apiClient.put(`/b2b/enterprises/${enterpriseId}`, {
        tech_emails: updatedEmails
      }, { headers: { Authorization: `Bearer ${localStorage.getItem("enterpriseToken")}` }});
      
      setEmails(updatedEmails);
      setNewEmail("");
      onUpdate(updatedEmails);
      toast.success("Tech email added!");
    } catch (err) {
      toast.error("Failed to add email");
    } finally {
      setSaving(false);
    }
  };
  
  const removeEmail = async (emailToRemove) => {
    setSaving(true);
    try {
      const updatedEmails = emails.filter(e => e !== emailToRemove);
      await apiClient.put(`/b2b/enterprises/${enterpriseId}`, {
        tech_emails: updatedEmails
      }, { headers: { Authorization: `Bearer ${localStorage.getItem("enterpriseToken")}` }});
      
      setEmails(updatedEmails);
      onUpdate(updatedEmails);
      toast.success("Email removed");
    } catch (err) {
      toast.error("Failed to remove email");
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="card-base p-6">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="w-5 h-5 text-purple-400" />
        <h3 className="font-bold text-viraly-light">Tech Team Emails</h3>
      </div>
      <p className="text-viraly-muted text-sm mb-4">
        Add tech team emails to receive Vira support chat transcripts and API notifications.
        These emails can also be used for tech team login via sandbox/production keys.
      </p>
      
      {/* Current emails */}
      <div className="space-y-2 mb-4">
        {emails.length === 0 ? (
          <p className="text-viraly-muted text-sm italic">No tech emails added yet</p>
        ) : (
          emails.map((email, idx) => (
            <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
              <span className="text-viraly-light">{email}</span>
              <button
                onClick={() => removeEmail(email)}
                disabled={saving}
                className="text-red-400 hover:text-red-300 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>
      
      {/* Add new email */}
      <div className="flex gap-2">
        <Input
          placeholder="tech@yourcompany.com"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addEmail()}
          className="flex-1 bg-white/5 border-white/10 text-white"
        />
        <Button
          onClick={addEmail}
          disabled={saving || !newEmail}
          className="bg-purple-600 hover:bg-purple-700"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add
        </Button>
      </div>
    </div>
  );
}

export default function EnterprisePortal() {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [enterprise, setEnterprise] = useState(null);
  const [apiKeys, setApiKeys] = useState(null);
  const [usage, setUsage] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [plans, setPlans] = useState(null);  // Dynamic pricing from API
  const [transactions, setTransactions] = useState([]);  // Billing history
  const [billingCycle, setBillingCycle] = useState("monthly");  // monthly or annual
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkoutCalculation, setCheckoutCalculation] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [autoSubscribe, setAutoSubscribe] = useState(false);  // Auto-renewal toggle
  
  // API Keys UI
  const [showSandboxKey, setShowSandboxKey] = useState(false);
  const [showProductionKey, setShowProductionKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  
  // Vira Chat
  const [viraMessages, setViraMessages] = useState([]);
  const [viraInput, setViraInput] = useState("");
  const [viraSending, setViraSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [escalationAvailable, setEscalationAvailable] = useState(false);
  
  // Add Merchant Modal
  const [showAddMerchant, setShowAddMerchant] = useState(false);
  const [newMerchant, setNewMerchant] = useState({ business_name: "", contact_name: "", email: "", monthly_post_quota: 10, monthly_video_quota: 2 });
  
  // Credit Allocation Modal
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [creditAllocation, setCreditAllocation] = useState({ post_credits: 0, video_credits: 0, reason: "" });
  
  // Phase 4: Brands & Locations State
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandLocations, setBrandLocations] = useState([]);
  const [showAddBrandModal, setShowAddBrandModal] = useState(false);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [brandLoading, setBrandLoading] = useState(false);
  const [newBrandData, setNewBrandData] = useState({ name: "", description: "", credit_pool_type: "shared" });
  const [newLocationData, setNewLocationData] = useState({ name: "", address: "", city: "", country: "Canada", timezone: "America/Toronto", manager_name: "", manager_email: "" });
  
  // Login state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginApiKey, setLoginApiKey] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  
  // Guide Modal
  const [showGuide, setShowGuide] = useState(false);
  
  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Fetch plans on mount and when enterprise data changes
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const country = enterprise?.country || "CA";
        const res = await apiClient.get(`/b2b/plans?country=${country}`);
        setPlans(res.data);
      } catch (err) {
        console.error("Failed to fetch plans:", err);
      }
    };
    fetchPlans();
  }, [enterprise?.country]);
  
  useEffect(() => {
    const token = localStorage.getItem("enterpriseToken");
    const enterpriseId = localStorage.getItem("enterpriseId");
    if (token && enterpriseId) {
      fetchEnterpriseData(enterpriseId, token);
    } else {
      setLoading(false);
    }
  }, [slug]);
  
  const fetchEnterpriseData = async (enterpriseId, token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [entRes, keysRes, usageRes, merchantsRes] = await Promise.all([
        apiClient.get(`/b2b/enterprises/${enterpriseId}`, { headers }),
        apiClient.get(`/b2b/enterprises/${enterpriseId}/api-keys`, { headers }),
        apiClient.get(`/b2b/enterprises/${enterpriseId}/usage`, { headers }),
        apiClient.get(`/b2b/merchants?enterprise_id=${enterpriseId}`, { headers })
      ]);
      
      setEnterprise(entRes.data);
      setApiKeys(keysRes.data);
      setUsage(usageRes.data);
      setMerchants(merchantsRes.data.merchants || []);
      setIsLoggedIn(true);
      
      // Fetch analytics after setting enterprise
      fetchAnalytics(enterpriseId, token);
    } catch (error) {
      console.error("Error fetching enterprise data:", error);
      localStorage.removeItem("enterpriseToken");
      localStorage.removeItem("enterpriseId");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAnalytics = async (enterpriseId, token) => {
    setAnalyticsLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await apiClient.get(`/b2b/enterprises/${enterpriseId}/analytics`, { headers });
      setAnalytics(res.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setAnalyticsLoading(false);
    }
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginApiKey) {
      toast.error("Please enter email and password");
      return;
    }
    
    setLoginLoading(true);
    try {
      const res = await apiClient.post("/b2b/enterprises/login", { 
        email: loginEmail, 
        password: loginApiKey 
      });
      
      if (res.data.enterprise_id) {
        localStorage.setItem("enterpriseToken", res.data.token || "enterprise-session");
        localStorage.setItem("enterpriseId", res.data.enterprise_id);
        await fetchEnterpriseData(res.data.enterprise_id, res.data.token);
        toast.success("Welcome!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoginLoading(false);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem("enterpriseToken");
    localStorage.removeItem("enterpriseId");
    setIsLoggedIn(false);
    setEnterprise(null);
  };
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!loginEmail) {
      toast.error("Please enter your email address first");
      return;
    }
    setForgotPasswordLoading(true);
    try {
      await apiClient.post("/b2b/forgot-password", {
        email: loginEmail,
        portal_type: "enterprise"
      });
      setForgotPasswordSent(true);
      toast.success("If an enterprise account exists, a reset link has been sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    } finally {
      setForgotPasswordLoading(false);
    }
  };
  
  const copyApiKey = (key, type) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(type);
    toast.success(`${type} API key copied`);
    setTimeout(() => setCopiedKey(null), 2000);
  };
  
  const sendViraMessage = async () => {
    if (!viraInput.trim()) return;
    
    const userMessage = viraInput;
    setViraMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setViraInput("");
    setViraSending(true);
    
    try {
      const res = await apiClient.post("/b2b/vira/chat", {
        enterprise_id: enterprise.id,
        message: userMessage,
        conversation_id: conversationId
      });
      
      setConversationId(res.data.conversation_id);
      setViraMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
      setEscalationAvailable(res.data.escalation_available);
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setViraSending(false);
    }
  };
  
  const escalateToHuman = async () => {
    if (!conversationId) return;
    
    try {
      const res = await apiClient.post("/b2b/vira/escalate", {
        conversation_id: conversationId,
        reason: viraMessages[viraMessages.length - 2]?.content || "Customer requested human support"
      });
      
      setViraMessages(prev => [...prev, {
        role: "system",
        content: `Your request has been escalated to our support team. Expected response time: ${res.data.expected_response_time}. Escalation ID: ${res.data.escalation_id}`
      }]);
      setEscalationAvailable(false);
      toast.success("Escalated to human support");
    } catch (error) {
      toast.error("Failed to escalate");
    }
  };
  
  const addMerchant = async () => {
    if (!newMerchant.business_name || !newMerchant.email) {
      toast.error("Please fill required fields");
      return;
    }
    
    try {
      const res = await apiClient.post("/b2b/merchants", {
        ...newMerchant,
        enterprise_id: enterprise.id
      }, { headers: { Authorization: `Bearer ${localStorage.getItem("enterpriseToken")}` }});
      
      toast.success("Merchant created!");
      setShowAddMerchant(false);
      setNewMerchant({ business_name: "", contact_name: "", email: "", monthly_post_quota: 10, monthly_video_quota: 2 });
      
      // Refresh merchants
      const merchantsRes = await apiClient.get(`/b2b/merchants?enterprise_id=${enterprise.id}`);
      setMerchants(merchantsRes.data.merchants || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create merchant");
    }
  };
  
  // Phase 4: Brands & Locations Functions
  const fetchBrands = async () => {
    if (!enterprise?.id) return;
    setBrandLoading(true);
    try {
      const res = await apiClient.get(`/b2b/enterprises/${enterprise.id}/brands`);
      setBrands(res.data.brands || []);
    } catch (error) {
      console.error("Failed to fetch brands:", error);
    } finally {
      setBrandLoading(false);
    }
  };
  
  const fetchBrandLocations = async (brandId) => {
    try {
      const res = await apiClient.get(`/b2b/brands/${brandId}/locations`);
      setBrandLocations(res.data.locations || []);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };
  
  const handleCreateBrand = async (e) => {
    e.preventDefault();
    if (!newBrandData.name) {
      toast.error("Brand name is required");
      return;
    }
    
    setBrandLoading(true);
    try {
      await apiClient.post(`/b2b/enterprises/${enterprise.id}/brands`, newBrandData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("enterpriseToken")}` }
      });
      toast.success("Brand created!");
      setShowAddBrandModal(false);
      setNewBrandData({ name: "", description: "", credit_pool_type: "shared" });
      fetchBrands();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create brand");
    } finally {
      setBrandLoading(false);
    }
  };
  
  const handleCreateLocation = async (e) => {
    e.preventDefault();
    if (!newLocationData.name || !selectedBrand) {
      toast.error("Location name is required");
      return;
    }
    
    setBrandLoading(true);
    try {
      await apiClient.post(`/b2b/brands/${selectedBrand.id}/locations`, {
        ...newLocationData,
        brand_id: selectedBrand.id
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("enterpriseToken")}` }
      });
      toast.success("Location created!");
      setShowAddLocationModal(false);
      setNewLocationData({ name: "", address: "", city: "", country: "Canada", timezone: "America/Toronto", manager_name: "", manager_email: "" });
      fetchBrandLocations(selectedBrand.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create location");
    } finally {
      setBrandLoading(false);
    }
  };
  
  // Fetch brands when tab changes
  useEffect(() => {
    if (activeTab === "brands" && enterprise?.id) {
      fetchBrands();
    }
  }, [activeTab, enterprise?.id]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-viraly-dark flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-viraly-orange animate-spin" />
      </div>
    );
  }
  
  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-viraly-dark flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-purple-600/20 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-purple-400" />
            </div>
            <h1 className="text-2xl font-bold text-viraly-light">Enterprise Portal</h1>
            <p className="text-viraly-muted mt-2">Access your API dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="card-base p-6 space-y-4">
            <div>
              <label className="text-sm text-viraly-muted block mb-1">Email Address</label>
              <Input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="you@company.com"
                className="bg-white/5 border-white/10"
                data-testid="enterprise-email-input"
              />
            </div>
            
            <div>
              <label className="text-sm text-viraly-muted block mb-1">Password</label>
              <Input
                type="password"
                value={loginApiKey}
                onChange={(e) => setLoginApiKey(e.target.value)}
                placeholder="Enter your password"
                className="bg-white/5 border-white/10"
                data-testid="enterprise-password-input"
              />
            </div>
            
            <Button type="submit" disabled={loginLoading} className="w-full btn-primary" data-testid="enterprise-login-btn">
              {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sign In
            </Button>
            
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="w-full text-sm text-viraly-muted hover:text-purple-400 transition-colors"
              data-testid="enterprise-forgot-password-link"
            >
              Forgot your password?
            </button>
          </form>
          
          {/* Forgot Password Modal */}
          <AnimatePresence>
            {showForgotPassword && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
                onClick={() => setShowForgotPassword(false)}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-viraly-card border border-white/10 rounded-2xl p-6 w-full max-w-md mx-4"
                >
                  {!forgotPasswordSent ? (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-viraly-light">Reset Password</h3>
                        <button
                          onClick={() => setShowForgotPassword(false)}
                          className="text-viraly-muted hover:text-white"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      
                      <p className="text-viraly-muted text-sm mb-4">
                        Enter your enterprise email address and we'll send you a link to reset your password.
                      </p>
                      
                      <form onSubmit={handleForgotPassword}>
                        <div className="mb-4">
                          <label className="text-sm text-viraly-light mb-2 block">Enterprise Email</label>
                          <Input
                            type="email"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            placeholder="you@company.com"
                            className="bg-white/5 border-white/10"
                            required
                            data-testid="enterprise-forgot-email-input"
                          />
                        </div>
                        
                        <div className="flex gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowForgotPassword(false)}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={forgotPasswordLoading}
                            className="flex-1 btn-primary"
                            data-testid="enterprise-forgot-submit-btn"
                          >
                            {forgotPasswordLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              "Send Reset Link"
                            )}
                          </Button>
                        </div>
                      </form>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                        <Check className="w-8 h-8 text-green-400" />
                      </div>
                      <h3 className="text-xl font-bold text-viraly-light mb-2">Check Your Email</h3>
                      <p className="text-viraly-muted text-sm mb-4">
                        If an enterprise account exists with this email, you will receive a password reset link shortly.
                      </p>
                      <Button
                        onClick={() => {
                          setShowForgotPassword(false);
                          setForgotPasswordSent(false);
                        }}
                        className="btn-primary"
                      >
                        Back to Login
                      </Button>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-viraly-dark flex">
      {/* Sidebar */}
      <aside className="w-64 bg-viraly-dark border-r border-white/10 p-4 flex flex-col">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h1 className="font-bold text-viraly-light text-sm truncate">{enterprise?.company_name}</h1>
            <p className="text-xs text-viraly-muted uppercase">{enterprise?.plan}</p>
          </div>
        </div>
        
        <nav className="flex-1 space-y-1">
          {/* How to Use Guide Button */}
          <button
            onClick={() => setShowGuide(true)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-purple-400 hover:text-white hover:bg-purple-500/10 border border-purple-500/30 mb-4"
            data-testid="enterprise-guide-btn"
          >
            <HelpCircle className="w-4 h-4" />
            How to Use
            <span className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-400">
              GUIDE
            </span>
          </button>
          
          {[
            { id: "dashboard", label: "Dashboard", icon: BarChart3 },
            { id: "api-keys", label: "API Keys", icon: Key },
            { id: "merchants", label: "Merchants", icon: Users },
            { id: "brands", label: "Brands & Locations", icon: Building2 },
            { id: "documents", label: "Documents", icon: FileText },
            { id: "plans", label: "Plans & Billing", icon: CreditCard },
            { id: "vira-support", label: "Vira On-Call", icon: MessageSquare },
            { id: "settings", label: "Settings", icon: Settings },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                activeTab === item.id
                  ? "bg-purple-500/20 text-purple-400"
                  : "text-viraly-muted hover:text-viraly-light hover:bg-white/5"
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </nav>
        
        <Button variant="outline" size="sm" className="mt-4" onClick={handleLogout}>
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div>
            <h2 className="text-2xl font-bold text-viraly-light mb-6">Dashboard</h2>
            
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="card-base p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-viraly-muted text-sm">Posts Used</span>
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">{usage?.posts?.percentage}%</span>
                </div>
                <p className="text-2xl font-bold text-viraly-light">{usage?.posts?.used} / {usage?.posts?.limit}</p>
                <div className="h-2 bg-white/10 rounded-full mt-3 overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${usage?.posts?.percentage || 0}%` }} />
                </div>
              </div>
              
              <div className="card-base p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-viraly-muted text-sm">Videos Used</span>
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded">{usage?.videos?.percentage}%</span>
                </div>
                <p className="text-2xl font-bold text-viraly-light">{usage?.videos?.used} / {usage?.videos?.limit}</p>
                <div className="h-2 bg-white/10 rounded-full mt-3 overflow-hidden">
                  <div className="h-full bg-purple-500 rounded-full" style={{ width: `${usage?.videos?.percentage || 0}%` }} />
                </div>
              </div>
              
              <div className="card-base p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-viraly-muted text-sm">Merchants</span>
                  {usage?.merchants?.unlimited ? (
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Unlimited</span>
                  ) : null}
                </div>
                <p className="text-2xl font-bold text-viraly-light">
                  {usage?.merchants?.count} {!usage?.merchants?.unlimited && `/ ${usage?.merchants?.limit}`}
                </p>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <button
                onClick={() => setActiveTab("api-keys")}
                className="card-base p-6 hover:border-purple-500/50 transition-all text-left"
              >
                <Key className="w-6 h-6 text-purple-400 mb-2" />
                <h4 className="font-bold text-viraly-light">API Keys</h4>
                <p className="text-sm text-viraly-muted">View and manage your API credentials</p>
              </button>
              
              <button
                onClick={() => setActiveTab("merchants")}
                className="card-base p-6 hover:border-purple-500/50 transition-all text-left"
              >
                <Users className="w-6 h-6 text-green-400 mb-2" />
                <h4 className="font-bold text-viraly-light">Add Merchant</h4>
                <p className="text-sm text-viraly-muted">Onboard a new merchant to your platform</p>
              </button>
            </div>
            
            {/* Analytics Widget */}
            <div className="card-base p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-viraly-light flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                  Merchant Analytics
                </h3>
                <button 
                  onClick={() => fetchAnalytics(enterprise?.id, localStorage.getItem("enterpriseToken"))}
                  className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                  disabled={analyticsLoading}
                >
                  <RefreshCw className={`w-4 h-4 text-viraly-muted ${analyticsLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              
              {analyticsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                </div>
              ) : analytics ? (
                <div className="space-y-6">
                  {/* Overview Stats */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-white/5 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-viraly-light">{analytics.overview?.active_merchants || 0}</p>
                      <p className="text-xs text-viraly-muted">Active Merchants</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-blue-400">{analytics.content_stats?.posts_this_month || 0}</p>
                      <p className="text-xs text-viraly-muted">Posts This Month</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-purple-400">{analytics.content_stats?.videos_this_month || 0}</p>
                      <p className="text-xs text-viraly-muted">Videos This Month</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-400">{analytics.credit_usage?.utilization_rate || 0}%</p>
                      <p className="text-xs text-viraly-muted">Credit Utilization</p>
                    </div>
                  </div>
                  
                  {/* Top Performing Merchants */}
                  {analytics.top_merchants && analytics.top_merchants.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-viraly-muted mb-3">Top Performing Merchants</h4>
                      <div className="space-y-2">
                        {analytics.top_merchants.map((m, idx) => (
                          <div key={m.id} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center text-xs text-purple-400">
                                {idx + 1}
                              </span>
                              <span className="text-sm text-viraly-light">{m.business_name}</span>
                            </div>
                            <div className="flex items-center gap-4 text-xs">
                              <span className="text-blue-400">{m.posts_this_month} posts</span>
                              <span className="text-purple-400">{m.videos_this_month} videos</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Package Distribution */}
                  {analytics.package_distribution && Object.keys(analytics.package_distribution).length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-viraly-muted mb-3">Package Distribution</h4>
                      <div className="flex gap-4">
                        {Object.entries(analytics.package_distribution).map(([pkg, count]) => (
                          <div key={pkg} className="flex-1 bg-white/5 rounded-lg p-3 text-center">
                            <p className="text-lg font-bold text-viraly-light">{count}</p>
                            <p className="text-xs text-viraly-muted capitalize">{pkg}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BarChart3 className="w-12 h-12 text-viraly-muted mx-auto mb-3" />
                  <p className="text-viraly-muted">No analytics data available yet</p>
                  <p className="text-xs text-viraly-muted mt-1">Add merchants to start seeing analytics</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* API Keys Tab */}
        {activeTab === "api-keys" && (
          <div>
            <h2 className="text-2xl font-bold text-viraly-light mb-6">API Keys</h2>
            
            <div className="space-y-4">
              {/* Sandbox Key */}
              <div className="card-base p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                      <Play className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-viraly-light">Sandbox API Key</h3>
                      <p className="text-xs text-viraly-muted">For testing and development</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded text-xs ${
                    apiKeys?.sandbox_api_key_active
                      ? "bg-green-500/20 text-green-400"
                      : "bg-red-500/20 text-red-400"
                  }`}>
                    {apiKeys?.sandbox_api_key_active ? "Active" : "Inactive"}
                  </span>
                </div>
                
                <div className="flex gap-3">
                  <div className="flex-1 bg-white/5 rounded-lg p-3 font-mono text-sm text-viraly-light truncate">
                    {showSandboxKey ? apiKeys?.sandbox_api_key : "vrl_test_••••••••••••••••••••••••"}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setShowSandboxKey(!showSandboxKey)}>
                    {showSandboxKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" onClick={() => copyApiKey(apiKeys?.sandbox_api_key, "Sandbox")}>
                    {copiedKey === "Sandbox" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                
                <p className="text-xs text-viraly-muted mt-3">
                  Limit: 100 calls/day • Calls made: {apiKeys?.sandbox_calls_count || 0}
                </p>
              </div>
              
              {/* Production Key */}
              <div className="card-base p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-viraly-light">Production API Key</h3>
                      <p className="text-xs text-viraly-muted">For live production use</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded text-xs ${
                    apiKeys?.production_api_key_active
                      ? "bg-green-500/20 text-green-400"
                      : "bg-yellow-500/20 text-yellow-400"
                  }`}>
                    {apiKeys?.production_api_key_active ? "Active" : "Requires Activation"}
                  </span>
                </div>
                
                {apiKeys?.production_api_key_active ? (
                  <div className="flex gap-3">
                    <div className="flex-1 bg-white/5 rounded-lg p-3 font-mono text-sm text-viraly-light truncate">
                      {showProductionKey ? apiKeys?.production_api_key : "vrl_live_••••••••••••••••••••••••"}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setShowProductionKey(!showProductionKey)}>
                      {showProductionKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" onClick={() => copyApiKey(apiKeys?.production_api_key, "Production")}>
                      {copiedKey === "Production" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                ) : (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Lock className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm font-medium text-yellow-400">Production Key Locked</span>
                    </div>
                    <p className="text-xs text-viraly-muted">
                      Complete {apiKeys?.sandbox_calls_required_for_production - (apiKeys?.sandbox_calls_count || 0)} more successful sandbox API calls to automatically unlock production access.
                    </p>
                    <div className="h-2 bg-white/10 rounded-full mt-3 overflow-hidden">
                      <div 
                        className="h-full bg-yellow-500 rounded-full transition-all" 
                        style={{ width: `${((apiKeys?.sandbox_calls_count || 0) / (apiKeys?.sandbox_calls_required_for_production || 10)) * 100}%` }} 
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Merchants Tab */}
        {activeTab === "merchants" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-viraly-light">Merchants</h2>
              <Button className="btn-primary" onClick={() => setShowAddMerchant(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Merchant
              </Button>
            </div>
            
            {merchants.length > 0 ? (
              <div className="card-base overflow-hidden">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr className="text-left text-viraly-muted text-sm">
                      <th className="p-4">Business Name</th>
                      <th className="p-4">Contact</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Credits Available</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {merchants.map(merchant => (
                      <tr key={merchant.id} className="border-t border-white/5 hover:bg-white/5">
                        <td className="p-4">
                          <p className="font-medium text-viraly-light">{merchant.business_name}</p>
                          <p className="text-xs text-viraly-muted">{merchant.access_code}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-viraly-light text-sm">{merchant.contact_name}</p>
                          <p className="text-xs text-viraly-muted">{merchant.email}</p>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            merchant.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {merchant.status}
                          </span>
                        </td>
                        <td className="p-4 text-sm">
                          <p className="text-viraly-light">{merchant.post_credits_available || merchant.monthly_post_quota} posts</p>
                          <p className="text-viraly-muted text-xs">{merchant.video_credits_available || merchant.monthly_video_quota} videos</p>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => {
                                setSelectedMerchant(merchant);
                                setShowCreditModal(true);
                              }}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Credits
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => window.open(`/m/${merchant.id}`, '_blank')}>
                              Portal
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="card-base p-8 text-center">
                <Users className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                <p className="text-viraly-muted">No merchants yet</p>
                <Button className="btn-primary mt-4" onClick={() => setShowAddMerchant(true)}>
                  Add First Merchant
                </Button>
              </div>
            )}
          </div>
        )}
        
        {/* Phase 4: Brands & Locations Tab */}
        {activeTab === "brands" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-viraly-light">Brands & Locations</h2>
                <p className="text-viraly-muted">Manage multiple brands and their locations</p>
              </div>
              <Button 
                className="bg-purple-600 hover:bg-purple-700"
                onClick={() => setShowAddBrandModal(true)}
                data-testid="add-brand-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Brand
              </Button>
            </div>
            
            {brandLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
              </div>
            ) : brands.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {brands.map(brand => (
                  <div 
                    key={brand.id} 
                    className={`card-base p-6 cursor-pointer transition-all hover:border-purple-500/50 ${selectedBrand?.id === brand.id ? 'border-purple-500' : ''}`}
                    onClick={() => {
                      setSelectedBrand(brand);
                      fetchBrandLocations(brand.id);
                    }}
                    data-testid={`brand-card-${brand.id}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <h3 className="font-bold text-viraly-light">{brand.name}</h3>
                          <p className="text-xs text-viraly-muted">{brand.description || 'No description'}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${brand.credit_pool_type === 'shared' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                        {brand.credit_pool_type === 'shared' ? 'Shared Credits' : 'Separate Credits'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-viraly-muted">Locations</p>
                        <p className="text-viraly-light font-semibold">{brand.location_count || 0}</p>
                      </div>
                      <div>
                        <p className="text-viraly-muted">Total Credits</p>
                        <p className="text-viraly-light font-semibold">{brand.total_credits || 0}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card-base p-12 text-center">
                <Building2 className="w-16 h-16 text-viraly-muted mx-auto mb-4" />
                <h3 className="text-xl font-bold text-viraly-light mb-2">No Brands Yet</h3>
                <p className="text-viraly-muted mb-6">Create your first brand to manage multiple locations under one umbrella</p>
                <Button 
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={() => setShowAddBrandModal(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Brand
                </Button>
              </div>
            )}
            
            {/* Selected Brand Locations */}
            {selectedBrand && (
              <div className="mt-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-viraly-light flex items-center gap-2">
                    <ChevronRight className="w-5 h-5 text-purple-400" />
                    Locations for {selectedBrand.name}
                  </h3>
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => setShowAddLocationModal(true)}
                    data-testid="add-location-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Location
                  </Button>
                </div>
                
                {brandLocations.length > 0 ? (
                  <div className="card-base overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr className="text-left text-viraly-muted text-sm">
                          <th className="p-4">Location</th>
                          <th className="p-4">City</th>
                          <th className="p-4">Manager</th>
                          <th className="p-4">Credits</th>
                          <th className="p-4">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brandLocations.map(location => (
                          <tr key={location.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`location-row-${location.id}`}>
                            <td className="p-4">
                              <p className="font-medium text-viraly-light">{location.name}</p>
                              <p className="text-xs text-viraly-muted">{location.address || 'No address'}</p>
                            </td>
                            <td className="p-4 text-viraly-light">{location.city || '-'}</td>
                            <td className="p-4">
                              <p className="text-viraly-light">{location.manager_name || '-'}</p>
                              <p className="text-xs text-viraly-muted">{location.manager_email || ''}</p>
                            </td>
                            <td className="p-4 text-viraly-light">
                              {selectedBrand.credit_pool_type === 'shared' ? (
                                <span className="text-xs text-blue-400">Uses Brand Pool</span>
                              ) : (
                                <span>{location.credits_allocated || 0}</span>
                              )}
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded text-xs ${location.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                {location.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="card-base p-8 text-center">
                    <Store className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                    <p className="text-viraly-muted">No locations yet for this brand</p>
                    <Button 
                      size="sm"
                      className="mt-4"
                      variant="outline"
                      onClick={() => setShowAddLocationModal(true)}
                    >
                      Add First Location
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* Add Brand Modal */}
            <AnimatePresence>
              {showAddBrandModal && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
                  onClick={() => setShowAddBrandModal(false)}
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-viraly-card border border-white/10 rounded-2xl p-6 w-full max-w-md mx-4"
                    data-testid="add-brand-modal"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-viraly-light">Create Brand</h3>
                      <button onClick={() => setShowAddBrandModal(false)} className="text-viraly-muted hover:text-white">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <form onSubmit={handleCreateBrand} className="space-y-4">
                      <div>
                        <label className="text-sm text-viraly-muted mb-1 block">Brand Name *</label>
                        <Input
                          value={newBrandData.name}
                          onChange={(e) => setNewBrandData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Coffee Chain North"
                          className="bg-white/5 border-white/10"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm text-viraly-muted mb-1 block">Description</label>
                        <Input
                          value={newBrandData.description}
                          onChange={(e) => setNewBrandData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Brief description"
                          className="bg-white/5 border-white/10"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm text-viraly-muted mb-1 block">Credit Pool Type</label>
                        <select
                          value={newBrandData.credit_pool_type}
                          onChange={(e) => setNewBrandData(prev => ({ ...prev, credit_pool_type: e.target.value }))}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-viraly-light"
                        >
                          <option value="shared">Shared Pool (all locations share credits)</option>
                          <option value="separate">Separate (each location has own allocation)</option>
                        </select>
                      </div>
                      
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setShowAddBrandModal(false)}>Cancel</Button>
                        <Button type="submit" disabled={brandLoading} className="bg-purple-600 hover:bg-purple-700">
                          {brandLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                          Create Brand
                        </Button>
                      </div>
                    </form>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Add Location Modal */}
            <AnimatePresence>
              {showAddLocationModal && selectedBrand && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
                  onClick={() => setShowAddLocationModal(false)}
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-viraly-card border border-white/10 rounded-2xl p-6 w-full max-w-lg mx-4"
                    data-testid="add-location-modal"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-xl font-bold text-viraly-light">Add Location</h3>
                        <p className="text-sm text-viraly-muted">For {selectedBrand.name}</p>
                      </div>
                      <button onClick={() => setShowAddLocationModal(false)} className="text-viraly-muted hover:text-white">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <form onSubmit={handleCreateLocation} className="space-y-4">
                      <div>
                        <label className="text-sm text-viraly-muted mb-1 block">Location Name *</label>
                        <Input
                          value={newLocationData.name}
                          onChange={(e) => setNewLocationData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Downtown Branch"
                          className="bg-white/5 border-white/10"
                          required
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-viraly-muted mb-1 block">City</label>
                          <Input
                            value={newLocationData.city}
                            onChange={(e) => setNewLocationData(prev => ({ ...prev, city: e.target.value }))}
                            placeholder="Toronto"
                            className="bg-white/5 border-white/10"
                          />
                        </div>
                        <div>
                          <label className="text-sm text-viraly-muted mb-1 block">Country</label>
                          <select
                            value={newLocationData.country}
                            onChange={(e) => setNewLocationData(prev => ({ ...prev, country: e.target.value }))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-viraly-light"
                          >
                            <option value="Canada">Canada</option>
                            <option value="United States">United States</option>
                            <option value="United Kingdom">United Kingdom</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm text-viraly-muted mb-1 block">Address</label>
                        <Input
                          value={newLocationData.address}
                          onChange={(e) => setNewLocationData(prev => ({ ...prev, address: e.target.value }))}
                          placeholder="123 Main Street"
                          className="bg-white/5 border-white/10"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-viraly-muted mb-1 block">Manager Name</label>
                          <Input
                            value={newLocationData.manager_name}
                            onChange={(e) => setNewLocationData(prev => ({ ...prev, manager_name: e.target.value }))}
                            placeholder="John Doe"
                            className="bg-white/5 border-white/10"
                          />
                        </div>
                        <div>
                          <label className="text-sm text-viraly-muted mb-1 block">Manager Email</label>
                          <Input
                            type="email"
                            value={newLocationData.manager_email}
                            onChange={(e) => setNewLocationData(prev => ({ ...prev, manager_email: e.target.value }))}
                            placeholder="john@example.com"
                            className="bg-white/5 border-white/10"
                          />
                        </div>
                      </div>
                      
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setShowAddLocationModal(false)}>Cancel</Button>
                        <Button type="submit" disabled={brandLoading} className="bg-purple-600 hover:bg-purple-700">
                          {brandLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                          Add Location
                        </Button>
                      </div>
                    </form>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
        
        {/* Plans & Billing Tab */}
        {activeTab === "plans" && (
          <div>
            <h2 className="text-2xl font-bold text-viraly-light mb-2">Plans & Billing</h2>
            <p className="text-viraly-muted mb-8">Manage your subscription and view usage</p>
            
            {/* Current Plan Card */}
            <div className="card-base p-6 mb-8 border-2 border-purple-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                    {enterprise?.plan === 'infinite' ? <Crown className="w-6 h-6 text-purple-400" /> :
                     enterprise?.plan === 'scaler' ? <Rocket className="w-6 h-6 text-blue-400" /> :
                     <Building className="w-6 h-6 text-green-400" />}
                  </div>
                  <div>
                    <p className="text-sm text-viraly-muted">Current Plan</p>
                    <h3 className="text-xl font-bold text-viraly-light capitalize">{plans?.plans?.[enterprise?.plan]?.name || enterprise?.plan} Plan</h3>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-viraly-light">
                    {plans?.plans?.[enterprise?.plan]?.price_monthly_display?.combined || `$${plans?.plans?.[enterprise?.plan]?.price_monthly_cad?.toLocaleString() || '0'} CAD`}
                    <span className="text-sm text-viraly-muted">/mo</span>
                  </p>
                  <p className="text-sm text-green-400">Active</p>
                </div>
              </div>
            </div>
            
            {/* Billing Cycle Toggle */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-viraly-light">Available Plans</h3>
              <div className="flex items-center gap-2 bg-white/5 rounded-lg p-1">
                <button
                  onClick={() => setBillingCycle("monthly")}
                  className={`px-4 py-2 rounded-md text-sm transition-colors ${
                    billingCycle === "monthly" ? "bg-purple-500 text-white" : "text-viraly-muted hover:text-viraly-light"
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle("annual")}
                  className={`px-4 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                    billingCycle === "annual" ? "bg-purple-500 text-white" : "text-viraly-muted hover:text-viraly-light"
                  }`}
                >
                  Annual
                  <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">Save up to 10%</span>
                </button>
              </div>
            </div>
            
            {plans?.credit_usage_info && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4">
                <p className="text-xs text-blue-300">{plans.credit_usage_info.video_credit_hint}</p>
                {plans.credit_usage_info.capacity_note && (
                  <p className="text-xs text-viraly-muted mt-1">{plans.credit_usage_info.capacity_note}</p>
                )}
              </div>
            )}
            <div className="grid grid-cols-3 gap-6 mb-8">
              {plans?.plans && Object.entries(plans.plans).map(([planId, plan]) => {
                const isCurrent = enterprise?.plan === planId;
                const isUpgrade = (enterprise?.plan === 'builder' && planId !== 'builder') || 
                                  (enterprise?.plan === 'scaler' && planId === 'infinite');
                const PlanIcon = planId === 'infinite' ? Crown : planId === 'scaler' ? Rocket : Building;
                const iconColor = planId === 'infinite' ? 'text-purple-400' : planId === 'scaler' ? 'text-blue-400' : 'text-green-400';
                const priceDisplay = billingCycle === "annual" ? plan.price_annual_display : plan.price_monthly_display;
                const monthlyEquivalent = billingCycle === "annual" ? Math.round(plan.price_annual_cad / 12) : plan.price_monthly_cad;
                
                return (
                  <div key={planId} className={`card-base p-6 pt-8 relative ${isCurrent ? 'border-2 border-purple-500/50' : ''}`}>
                    {plan.popular && (
                      <div className="absolute top-2 right-2 bg-blue-500 text-white text-[10px] px-2 py-0.5 rounded-full">
                        Popular
                      </div>
                    )}
                    {billingCycle === "annual" && plan.annual_discount_percent > 0 && (
                      <div className="absolute top-2 left-2 bg-green-500/20 text-green-400 text-[10px] px-2 py-0.5 rounded-full">
                        Save {plan.annual_discount_percent}%
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-4">
                      <PlanIcon className={`w-6 h-6 ${iconColor}`} />
                      <h4 className="text-lg font-bold text-viraly-light">{plan.name}</h4>
                    </div>
                    {billingCycle === "annual" ? (
                      <div className="mb-1">
                        <p className="text-3xl font-bold text-viraly-light">
                          {priceDisplay?.combined || `$${plan.price_annual_cad?.toLocaleString()} CAD`}
                          <span className="text-sm text-viraly-muted">/year</span>
                        </p>
                        <p className="text-xs text-green-400">${monthlyEquivalent}/mo equivalent</p>
                      </div>
                    ) : (
                      <p className="text-3xl font-bold text-viraly-light mb-1">
                        {priceDisplay?.combined || `$${plan.price_monthly_cad?.toLocaleString()} CAD`}
                        <span className="text-sm text-viraly-muted">/mo</span>
                      </p>
                    )}
                    <p className="text-sm text-viraly-muted mb-4">{plan.description}</p>
                    <ul className="space-y-2 mb-6 text-sm">
                      <li className="flex items-center gap-2 text-viraly-light">
                        <Check className="w-4 h-4 text-green-400" /> {(plan.monthly_post_credits * (billingCycle === "annual" ? 12 : 1))?.toLocaleString()} post credits{billingCycle === "annual" ? "/year" : "/month"}
                      </li>
                      <li className="flex items-center gap-2 text-viraly-light">
                        <Check className="w-4 h-4 text-green-400" /> {plan.monthly_video_credits * (billingCycle === "annual" ? 12 : 1)} video credits{billingCycle === "annual" ? "/year" : "/month"}
                      </li>
                      <li className="flex items-center gap-2 text-viraly-light">
                        <Check className="w-4 h-4 text-green-400" /> {plan.max_merchants === -1 ? 'Unlimited' : `Up to ${plan.max_merchants}`} merchants
                      </li>
                      <li className="flex items-center gap-2 text-viraly-light">
                        <Check className="w-4 h-4 text-green-400" /> {plan.competitor_monitoring_slots} competitor monitoring slots
                      </li>
                      <li className="flex items-center gap-2 text-viraly-light">
                        <Check className="w-4 h-4 text-green-400" /> 24x7 Vira On-Call{plan.priority_support ? ' + Priority' : ''}
                      </li>
                      {plan.features?.filter(f => !f.includes('Competitor')).map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-viraly-light">
                          <Check className="w-4 h-4 text-green-400" /> {feature}
                        </li>
                      ))}
                    </ul>
                    {isCurrent ? (
                      <Button className="w-full" disabled>Current Plan</Button>
                    ) : (
                      <Button 
                        className={`w-full ${isUpgrade ? 'btn-primary' : ''}`} 
                        variant={isUpgrade ? 'default' : 'outline'}
                        onClick={async () => {
                          setSelectedPlan({ ...plan, planId });
                          try {
                            const res = await apiClient.post("/b2b/checkout/calculate", {
                              enterprise_id: enterprise.id,
                              plan_id: planId,
                              billing_cycle: billingCycle,
                              country: enterprise.country || "CA",
                              province: enterprise.province || "ON"
                            });
                            setCheckoutCalculation(res.data);
                            setShowCheckoutModal(true);
                          } catch (err) {
                            toast.error("Failed to calculate checkout");
                          }
                        }}
                      >
                        {isUpgrade ? 'Upgrade' : 'Downgrade'}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Usage This Month */}
            <h3 className="font-bold text-viraly-light mb-4">Current Usage</h3>
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="card-base p-4">
                <p className="text-sm text-viraly-muted">Posts Used</p>
                <p className="text-2xl font-bold text-viraly-light">
                  {usage?.posts?.used || 0} <span className="text-sm text-viraly-muted">/ {usage?.posts?.limit || 0}</span>
                </p>
                <div className="h-2 bg-white/10 rounded-full mt-2">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: `${((usage?.posts?.used || 0) / (usage?.posts?.limit || 1)) * 100}%` }} />
                </div>
              </div>
              <div className="card-base p-4">
                <p className="text-sm text-viraly-muted">Videos Used</p>
                <p className="text-2xl font-bold text-viraly-light">
                  {usage?.videos?.used || 0} <span className="text-sm text-viraly-muted">/ {usage?.videos?.limit || 0}</span>
                </p>
                <div className="h-2 bg-white/10 rounded-full mt-2">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${((usage?.videos?.used || 0) / (usage?.videos?.limit || 1)) * 100}%` }} />
                </div>
              </div>
              <div className="card-base p-4">
                <p className="text-sm text-viraly-muted">Active Merchants</p>
                <p className="text-2xl font-bold text-viraly-light">
                  {usage?.merchants?.used || merchants.length} <span className="text-sm text-viraly-muted">/ {usage?.merchants?.limit === -1 ? '∞' : usage?.merchants?.limit || 0}</span>
                </p>
                <div className="h-2 bg-white/10 rounded-full mt-2">
                  <div className="h-full bg-purple-500 rounded-full" style={{ width: usage?.merchants?.limit === -1 ? '10%' : `${((usage?.merchants?.used || 0) / (usage?.merchants?.limit || 1)) * 100}%` }} />
                </div>
              </div>
            </div>
            
            {/* Payment Method */}
            <h3 className="font-bold text-viraly-light mb-4">Payment Method</h3>
            <div className="card-base p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-10 bg-blue-600 rounded flex items-center justify-center text-white text-xs font-bold">PayPal</div>
                  <div>
                    <p className="text-sm text-viraly-light">PayPal Business Account</p>
                    <p className="text-xs text-viraly-muted">Billing email: {enterprise?.email}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm">
                  Update
                </Button>
              </div>
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-xs text-viraly-muted">
                  Next billing date: {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()} • 
                  Amount: ${plans?.plans?.[enterprise?.plan]?.price_cad?.toLocaleString() || '0'} CAD
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Documents Tab */}
        {activeTab === "documents" && (
          <DocumentsSection
            entityType="enterprise"
            entityId={enterprise?.id}
            entityName={enterprise?.contact_name}
            entityEmail={enterprise?.email}
            entityTitle={enterprise?.contact_title || "Representative"}
            accentColor="purple"
          />
        )}
        
        {/* Vira Support Tab */}
        {activeTab === "vira-support" && (
          <div className="flex flex-col h-[calc(100vh-8rem)]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-viraly-light">Vira On-Call</h2>
                <p className="text-viraly-muted text-sm">Your AI support assistant</p>
              </div>
              <div className="flex gap-2">
                {viraMessages.length > 0 && (
                  <Button 
                    variant="outline" 
                    className="text-green-400 border-green-400/50 hover:bg-green-500/10"
                    onClick={async () => {
                      if (!conversationId) return;
                      try {
                        await apiClient.post("/b2b/vira/end-conversation", {
                          conversation_id: conversationId,
                          send_transcript: true
                        });
                        toast.success("Chat ended! Transcript sent to your tech emails.");
                        setViraMessages([]);
                        setConversationId(null);
                        setEscalationAvailable(false);
                      } catch (err) {
                        toast.error("Failed to end conversation");
                      }
                    }}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    End & Send Transcript
                  </Button>
                )}
                {escalationAvailable && (
                  <Button variant="outline" className="text-yellow-400 border-yellow-400/50" onClick={escalateToHuman}>
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Escalate to Human
                  </Button>
                )}
              </div>
            </div>
            
            {/* Tech emails notice */}
            {(!enterprise?.tech_emails || enterprise.tech_emails.length === 0) && viraMessages.length === 0 && (
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-purple-400" />
                  <p className="text-sm text-purple-300">
                    <span className="font-medium">Tip:</span> Add tech emails in Settings to receive chat transcripts via email.
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex-1 card-base p-4 overflow-auto mb-4">
              {viraMessages.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                    <p className="text-viraly-muted">Start a conversation with Vira</p>
                    <p className="text-xs text-viraly-muted mt-2">Ask about API integration, billing, or any issues</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {viraMessages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] p-3 rounded-xl ${
                        msg.role === 'user' 
                          ? 'bg-purple-500/20 text-viraly-light' 
                          : msg.role === 'system'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-white/10 text-viraly-light'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        {msg.timestamp && (
                          <p className="text-xs text-viraly-muted mt-1">{new Date(msg.timestamp).toLocaleTimeString()}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {viraSending && (
                    <div className="flex justify-start">
                      <div className="bg-white/10 p-3 rounded-xl">
                        <Loader2 className="w-4 h-4 animate-spin text-viraly-muted" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              <Input
                value={viraInput}
                onChange={(e) => setViraInput(e.target.value)}
                placeholder="Ask Vira anything..."
                className="bg-white/5 border-white/10"
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendViraMessage()}
              />
              <Button onClick={sendViraMessage} disabled={viraSending || !viraInput.trim()}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
        
        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-viraly-light mb-6">Settings</h2>
            
            {/* Account Information */}
            <div className="card-base p-6">
              <h3 className="font-bold text-viraly-light mb-4">Account Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-viraly-muted">Company</span>
                  <span className="text-viraly-light">{enterprise?.company_name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-viraly-muted">Contact</span>
                  <span className="text-viraly-light">{enterprise?.contact_name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-viraly-muted">Primary Email</span>
                  <span className="text-viraly-light">{enterprise?.email}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-viraly-muted">Plan</span>
                  <span className="text-purple-400 uppercase">{enterprise?.plan}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-viraly-muted">Status</span>
                  <span className={enterprise?.status === 'active' ? 'text-green-400' : 'text-yellow-400'}>
                    {enterprise?.status}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Tech Emails - for support transcripts */}
            <TechEmailsManager 
              techEmails={enterprise?.tech_emails || []}
              enterpriseId={enterprise?.id}
              onUpdate={(newEmails) => setEnterprise({ ...enterprise, tech_emails: newEmails })}
            />
          </div>
        )}
      </main>
      
      {/* Add Merchant Modal */}
      <AnimatePresence>
        {showAddMerchant && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddMerchant(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-viraly-card rounded-2xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-viraly-light mb-4">Add New Merchant</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-viraly-muted block mb-1">Business Name *</label>
                  <Input
                    value={newMerchant.business_name}
                    onChange={(e) => setNewMerchant({ ...newMerchant, business_name: e.target.value })}
                    placeholder="Merchant Business Inc."
                  />
                </div>
                <div>
                  <label className="text-sm text-viraly-muted block mb-1">Contact Name</label>
                  <Input
                    value={newMerchant.contact_name}
                    onChange={(e) => setNewMerchant({ ...newMerchant, contact_name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="text-sm text-viraly-muted block mb-1">Email *</label>
                  <Input
                    type="email"
                    value={newMerchant.email}
                    onChange={(e) => setNewMerchant({ ...newMerchant, email: e.target.value })}
                    placeholder="merchant@business.com"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-viraly-muted block mb-1">Monthly Posts</label>
                    <Input
                      type="number"
                      value={newMerchant.monthly_post_quota}
                      onChange={(e) => setNewMerchant({ ...newMerchant, monthly_post_quota: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-viraly-muted block mb-1">Monthly Videos</label>
                    <Input
                      type="number"
                      value={newMerchant.monthly_video_quota}
                      onChange={(e) => setNewMerchant({ ...newMerchant, monthly_video_quota: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setShowAddMerchant(false)}>
                  Cancel
                </Button>
                <Button className="flex-1 btn-primary" onClick={addMerchant}>
                  Create Merchant
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Credit Allocation Modal */}
      <AnimatePresence>
        {showCreditModal && selectedMerchant && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => { setShowCreditModal(false); setSelectedMerchant(null); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-viraly-card rounded-2xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-viraly-light mb-2">Add Credits</h3>
              <p className="text-sm text-viraly-muted mb-4">
                Allocate credits to <span className="text-viraly-light">{selectedMerchant.business_name}</span>
              </p>
              
              <div className="bg-white/5 rounded-lg p-3 mb-4">
                <p className="text-xs text-viraly-muted">Current balance</p>
                <p className="text-viraly-light">
                  {selectedMerchant.post_credits_available || 0} post credits, {selectedMerchant.video_credits_available || 0} video credits
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-viraly-muted block mb-1">Post Credits</label>
                    <Input
                      type="number"
                      min="0"
                      value={creditAllocation.post_credits}
                      onChange={(e) => setCreditAllocation({ ...creditAllocation, post_credits: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-viraly-muted block mb-1">Video Credits</label>
                    <Input
                      type="number"
                      min="0"
                      value={creditAllocation.video_credits}
                      onChange={(e) => setCreditAllocation({ ...creditAllocation, video_credits: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-viraly-muted block mb-1">Reason (optional)</label>
                  <Input
                    value={creditAllocation.reason}
                    onChange={(e) => setCreditAllocation({ ...creditAllocation, reason: e.target.value })}
                    placeholder="e.g., Monthly allocation, Payment received"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => { setShowCreditModal(false); setSelectedMerchant(null); }}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 btn-primary"
                  onClick={async () => {
                    try {
                      const headers = { Authorization: `Bearer ${localStorage.getItem("enterpriseToken")}` };
                      await apiClient.post(`/b2b/enterprises/${enterprise.id}/allocate-credits`, {
                        merchant_id: selectedMerchant.id,
                        post_credits: creditAllocation.post_credits,
                        video_credits: creditAllocation.video_credits,
                        reason: creditAllocation.reason || "Manual allocation"
                      }, { headers });
                      toast.success("Credits allocated successfully!");
                      setShowCreditModal(false);
                      setSelectedMerchant(null);
                      setCreditAllocation({ post_credits: 0, video_credits: 0, reason: "" });
                      // Refresh merchants
                      const merchantsRes = await apiClient.get(`/b2b/merchants?enterprise_id=${enterprise.id}`);
                      setMerchants(merchantsRes.data.merchants || []);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Failed to allocate credits");
                    }
                  }}
                >
                  Allocate Credits
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* PayPal Checkout Modal */}
      <AnimatePresence>
        {showCheckoutModal && selectedPlan && checkoutCalculation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => { setShowCheckoutModal(false); setSelectedPlan(null); setCheckoutCalculation(null); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-viraly-card rounded-2xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-viraly-light mb-2">Confirm Subscription</h3>
              <p className="text-sm text-viraly-muted mb-4">
                {checkoutCalculation.plan_name} Plan ({checkoutCalculation.billing_cycle})
              </p>
              
              {/* Order Summary */}
              <div className="bg-white/5 rounded-lg p-4 mb-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-viraly-muted">Plan Price</span>
                  <span className="text-viraly-light">
                    {checkoutCalculation.base_price_display?.combined || `$${checkoutCalculation.base_price_cad?.toLocaleString()} CAD`}
                  </span>
                </div>
                {checkoutCalculation.tax && (
                  <div className="flex justify-between text-sm">
                    <span className="text-viraly-muted">{checkoutCalculation.tax.info?.type} ({(checkoutCalculation.tax.info?.rate * 100).toFixed(1)}%)</span>
                    <span className="text-viraly-light">
                      {checkoutCalculation.tax.display?.combined || `$${checkoutCalculation.tax.amount?.toFixed(2)} CAD`}
                    </span>
                  </div>
                )}
                {checkoutCalculation.gateway_fee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-viraly-muted">Payment Processing</span>
                    <span className="text-viraly-light">
                      {checkoutCalculation.gateway_fee_display?.combined || `$${checkoutCalculation.gateway_fee?.toFixed(2)} CAD`}
                    </span>
                  </div>
                )}
                <div className="border-t border-white/10 pt-2 mt-2">
                  <div className="flex justify-between font-bold">
                    <span className="text-viraly-light">Total</span>
                    <span className="text-viraly-light">
                      {checkoutCalculation.total_display?.combined || `$${checkoutCalculation.total_cad?.toLocaleString()} CAD`}
                    </span>
                  </div>
                </div>
                {checkoutCalculation.paypal_will_charge && (
                  <div className="text-xs text-viraly-muted mt-2 pt-2 border-t border-white/5">
                    PayPal will charge: <span className="text-viraly-light font-medium">${checkoutCalculation.paypal_will_charge.amount?.toLocaleString()} CAD</span>
                  </div>
                )}
              </div>
              
              {/* Credits included */}
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 mb-4">
                <p className="text-xs text-green-400 font-medium mb-1">Credits Included:</p>
                <p className="text-sm text-viraly-light">
                  {checkoutCalculation.credits_included?.post_credits?.toLocaleString()} post credits + {checkoutCalculation.credits_included?.video_credits?.toLocaleString()} video credits
                </p>
                {checkoutCalculation.credits_included?.competitor_monitoring_slots && (
                  <p className="text-xs text-viraly-muted mt-1">
                    + {checkoutCalculation.credits_included.competitor_monitoring_slots} competitor monitoring slots
                  </p>
                )}
              </div>
              
              {checkoutCalculation.annual_discount_percent > 0 && (
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 mb-4">
                  <p className="text-xs text-purple-400">You're saving {checkoutCalculation.annual_discount_percent}% with annual billing!</p>
                </div>
              )}
              
              {/* Auto-subscribe option */}
              <div className="bg-white/5 rounded-lg p-3 mb-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={autoSubscribe}
                    onChange={(e) => setAutoSubscribe(e.target.checked)}
                    className="w-4 h-4 rounded border-white/20 bg-white/5"
                  />
                  <div>
                    <p className="text-sm text-viraly-light">Enable Auto-Subscribe</p>
                    <p className="text-xs text-viraly-muted">Automatically renew when subscription expires</p>
                  </div>
                </label>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => { setShowCheckoutModal(false); setSelectedPlan(null); setCheckoutCalculation(null); }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 btn-primary"
                  disabled={processingPayment}
                  onClick={async () => {
                    setProcessingPayment(true);
                    try {
                      // Create PayPal order
                      const orderRes = await apiClient.post("/b2b/checkout/create-paypal-order", {
                        enterprise_id: enterprise.id,
                        plan_id: selectedPlan.planId,
                        billing_cycle: billingCycle,
                        country: enterprise.country || "CA",
                        province: enterprise.province || "ON",
                        auto_subscribe: autoSubscribe
                      });
                      
                      // For demo purposes, we'll simulate the capture
                      // In production, you'd redirect to PayPal or use PayPal JS SDK
                      const captureRes = await apiClient.post(`/b2b/checkout/capture/${orderRes.data.order_id}`);
                      
                      toast.success("Subscription activated successfully!");
                      setShowCheckoutModal(false);
                      setSelectedPlan(null);
                      setCheckoutCalculation(null);
                      
                      // Refresh enterprise data
                      const entRes = await apiClient.get(`/b2b/enterprises/${enterprise.id}`);
                      setEnterprise(entRes.data);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Payment failed. Please try again.");
                    } finally {
                      setProcessingPayment(false);
                    }
                  }}
                >
                  {processingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Pay with PayPal
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* How to Use Guide Modal */}
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-viraly-card border-viraly-border">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading flex items-center gap-3">
              <BookOpen className="w-7 h-7 text-purple-400" />
              <span className="text-purple-400">Enterprise Guide</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {enterpriseGuideSteps.map((step, index) => (
              <div 
                key={index}
                className="bg-viraly-dark/50 rounded-xl p-5 border border-white/5 hover:border-purple-500/30 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-purple-500/10 text-purple-400">
                    <step.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-viraly-muted mb-3">{step.description}</p>
                    <div className="space-y-1">
                      {step.tips.map((tip, tipIdx) => (
                        <div key={tipIdx} className="flex items-center gap-2 text-sm text-viraly-muted/80">
                          <Zap className="w-3 h-3 text-purple-400" />
                          <span>{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Downloadable Guides */}
            <div className="bg-purple-500/10 rounded-xl p-5 border border-purple-500/20">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-400" />
                Download Guides
              </h3>
              <p className="text-viraly-muted text-sm mb-4">
                Get comprehensive onboarding guides for you and your merchants.
              </p>
              <div className="flex flex-wrap gap-3">
                <a 
                  href="/downloads/Viraly_Enterprise_Onboarding_Guide.docx"
                  download
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Enterprise Guide
                </a>
                <a 
                  href="/downloads/Viraly_Merchant_Onboarding_Guide.docx"
                  download
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Merchant Guide (Share with merchants)
                </a>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
